import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { postManualUpload, getDevice } from '../../services/endpoints'
import { NetworkRequestError } from '../../services/lib'

const sliceName = 'manualUploads'

export type ManualUploadState = {
    data: ManualUploadData
    error: null | NetworkRequestError
    loading: boolean
}

export type ManualUploadData = {
  type: 'time' | 'sector' | 'raw'
  start: null | undefined | Date | number
  end: null | undefined | Date | number
  fbMask: null | undefined | number,
  fleetId?: null | undefined | number
  vehicleId?: null | undefined | number
  deviceId?: null | number
  deviceType?: null | string
  name?: null | string
  serial?: null | string
}

export const postManualUploads = createAsyncThunk(
  `${sliceName}/postManualUploads`,
  async ({type, start, end, fbMask, deviceId}:ManualUploadData, { rejectWithValue}) => { 
    try{
      return await postManualUpload(`${type}|${start}|${end}|${fbMask}`, `${deviceId}`)
    } catch (err: unknown) {
      return rejectWithValue(err)
    }
  }
) 

export const getDeviceInfo = createAsyncThunk(
  `${sliceName}/getDeviceInfo`,
  async (id:string | unknown, { rejectWithValue}) => { 
    try{
      return await getDevice(Number(id))
    } catch (err: unknown) {
      return rejectWithValue(err)
    }
  }
) 

export const initialManualUploadsState: ManualUploadState = {
    data: {
      type: 'time',
      start: null,
      end: null,
      fbMask: null,
      fleetId: null,
      vehicleId: null,
      deviceId: null,
      deviceType: null,
      serial: null,
      name: null
    },
    error: null,
    loading: false
}

export const manualUploadsSlice = createSlice({
    name: sliceName,
    initialState: initialManualUploadsState,
    reducers: {
      setError: (state) => {
        state.error = {
          'code': 'error',
          'fields': [],
          'message': 'form error',
          'name': 'NetworkRequestError'
        }
        return state
      },
      resetError: (state) => {
        state.error = null
        return state
      },
      setData: (state, action) => {
        state.data = {
          type: action.payload.type ? action.payload.type : state.data.type,
          start: action.payload.start ? action.payload.start : state.data.start,
          end: action.payload.end ? action.payload.end : state.data.end,
          fbMask: action.payload.fbMask ? action.payload.fbMask : state.data.fbMask,
          fleetId:  action.payload.fleetId ? action.payload.fleetId : state.data.fleetId,
          vehicleId: action.payload.vehicleId ? action.payload.vehicleId : state.data.vehicleId
        }
        state.error = null
        state.loading = false
        return state
      },
      setInitialState: (state) => {
        state.data = {
          type: 'time',
          start: null,
          end: null,
          fbMask: null,
          vehicleId: null,
          fleetId: null
        }
        state.error = null
        state.loading = false
      }
    },
    extraReducers(builder) {
      builder
        .addCase(
          postManualUploads.fulfilled,
          (state, action) => {
            const args = action.payload.arguments.split('|')
            state.data.type = args[0] as 'time' | 'sector' | 'raw'
            state.data.start = args[1]
            state.data.end = args[2]
            state.data.fbMask = args[3] ? Number(args[3]) : null
            state.error = null  
            state.loading = false
        })
        .addCase(postManualUploads.pending, (state, _action) => {
          state.loading = true
        })
        .addCase(postManualUploads.rejected, (state, action) => {
          state.error = action.payload as NetworkRequestError
          state.loading = false
        })
        .addCase(
            getDeviceInfo.fulfilled,
          (state, action) => {
            state.data.deviceId = action.payload.id
            state.data.fleetId = action.payload.vehicle.fleetId
            state.data.vehicleId = action.payload.vehicle.id
            state.data.deviceType = action.payload.type
            state.data.serial = action.payload.serial
            state.data.name = action.payload.vehicle.name
            state.error = null  
            state.loading = false
        })
        .addCase(getDeviceInfo.pending, (state, _action) => {
          state.loading = true
        })
        .addCase(getDeviceInfo.rejected, (state, action) => {
          state.error = action.payload as NetworkRequestError
          state.loading = false
        })
    },
  })